import {BaseService} from '@esgi/core/service';
import {getUser} from '@esgi/core/authentication';
import {Test} from '@esgi/main/kits/reports';
import {BehaviorSubject, Observable, combineLatest, debounceTime, switchMap, of, tap} from 'rxjs';
import {StudentWithUnit, Settings, UnitDataSettings, PrintSettings, ReportRequest, DownloadRequest, MarkingPeriod, MarkingPeriodItem, NotTested, Grade, Report, FileType} from '../types';
import {GenerateService} from './generate-service';
import {DownloadService} from './download-service';
import {StudentService} from './student-service';
import {SubjectService} from './subject-service';
import {TestService} from './test-service';
import {V2TeachersPagesReportsStudentDetailsController} from '@esgi/contracts/esgi';

export class StudentDetailService extends BaseService {
	public readonly students$ = new BehaviorSubject<StudentWithUnit[]>([]);
	public readonly subject$ = new BehaviorSubject(0);
	public readonly tests$ = new BehaviorSubject<Test['id'][]>([]);
	public readonly grades$ = new BehaviorSubject<Grade[]>([]);
	public readonly settings$ = new BehaviorSubject<Settings>({
		baseline: true,
		summary: false,
		notes: false,
		forward: false,
	});
	public readonly grade$ = new BehaviorSubject<boolean>(false);
	public readonly markingPeriod$ = new BehaviorSubject<MarkingPeriod>(MarkingPeriod.All);
	public readonly notTested$ = new BehaviorSubject<NotTested>(NotTested.NT);
	public readonly report$ = new BehaviorSubject<Report | null>(null);
	public readonly unitDataSettings$ = new BehaviorSubject<UnitDataSettings| null>(null);
	public readonly printSettings$ = new BehaviorSubject<PrintSettings | null>(null);
	public readonly inited$ = new BehaviorSubject(false);
	public readonly valid$: Observable<boolean> = this.completeOnDestroy(
		combineLatest([this.students$, this.tests$, this.inited$])
	).pipe(
		debounceTime(100),
		switchMap(([students, tests, inited]) => {
			return of(Boolean(students.length) && Boolean(tests.length) && inited);
		}),
	);
	public readonly busy$ = new BehaviorSubject(false);
	public readonly preview$ = new BehaviorSubject(false);
	public studentService = new StudentService();
	public subjectService = new SubjectService();
	public testService = new TestService();
	protected generateService = new GenerateService();
	protected downloadService = new DownloadService();
	protected currentUser = getUser();
	private readonly controller = new V2TeachersPagesReportsStudentDetailsController();

	constructor() {
		super();

		this.completeOnDestroy(
			combineLatest([this.subject$, this.students$])
		).pipe(
			debounceTime(500),
			tap(([subjectId, students]) => {
				if (!students.length) {
					return;
				}
				const subject = this.subjectService.get(subjectId);
				this.testService.getTests(subject, students);
			}),
		).subscribe();

		this.completeOnDestroy(
			combineLatest([this.tests$])
		).pipe(
			debounceTime(500),
			tap(() => this.init()),
		).subscribe();

		this.completeOnDestroy(
			this.subjectService.loaded$
		).pipe(tap((value) => {
			if (value) {
				this.subject$.next(this.subjectService.getByIndex(0)?.id || 0);
			}
		})).subscribe();

		this.completeOnDestroy(
			combineLatest([this.students$, this.tests$, this.subject$])
		).pipe(
			debounceTime(100),
			tap(([students, testIDs, subjectId]) => {
				if (!subjectId) {
					return;
				}

				const {
					id: subjectID,
					name,
					type: subjectType,
					level: subjectLevel,
				} = this.subjectService.get(subjectId);

				this.unitDataSettings$.next({
					subject: {
						subjectID,
						name,
						subjectType,
						subjectLevel,
					},
					testIDs,
					studentIDs: students.map(({studentId}) => studentId),
				});
			}),
		).subscribe();

		this.completeOnDestroy(
			combineLatest([
				this.settings$,
				this.grade$,
				this.markingPeriod$,
				this.notTested$,
			]),
		).pipe(
			debounceTime(100),
			tap(([settings, includeGradeScore, markingPeriod, notTested]) => {
				const {
					baseline: showBaseline,
					summary: includeSummaryNotes,
					notes: includeNotes,
					forward: carryScores,
				} = settings || {};
				this.printSettings$.next({
					...this.printSettings$.value,
					currentPeriod: markingPeriod === MarkingPeriod.Current,
					markingPeriods: this.getMarkingPeriods(),
					showBaseline,
					includeNotes,
					includeSummaryNotes,
					carryScores,
					includeGradeScore,
					notTestedDisplayValue: notTested === NotTested.NT ? 'NT' : '0',
				});
			}),
		).subscribe();

		this.completeOnDestroy(
			combineLatest([this.downloadService.busy])
		).pipe(
			debounceTime(100),
			tap(([busy]) => this.busy$.next(busy)),
		).subscribe();
	}

	public destroy() {
		super.destroy();
		this.subjectService.destroy();
		this.testService.destroy();
		this.downloadService.destroy();
	}

	public override dispose() {
		this.controller.dispose();
	}

	public setStudents(value: StudentWithUnit[]) {
		this.students$.next(value);
	}

	public setSubject(value: number) {
		this.subject$.next(value);
	}

	public setTests(value: Test['id'][]) {
		this.tests$.next(value);
	}

	public setSettings(value: Settings) {
		const {baseline = true, forward = false} = this.settings$.value || {};

		if (baseline !== value.baseline) {
			this.controller.showBaseline({value: value.baseline}).subscribe();
		}

		if (forward !== value.forward && this.preview$.value) {
			const s = this.printSettings$.subscribe(() => {
				s.unsubscribe();
				this.onPreview(() => {});
			});
		}

		this.settings$.next(value);
	}

	public setGrade(value: boolean) {
		this.grade$.next(value);
	}

	public setMarkingPeriod(value: MarkingPeriod) {
		this.markingPeriod$.next(value);
	}

	public setNotTested(value: NotTested) {
		this.notTested$.next(value);
	}

	public setPreview(value: boolean) {
		this.preview$.next(value);
	}

	public onPreview(callback: VoidFunction) {
		const generateSettings = this.generateReport();
		this.downloadService.preview(generateSettings as ReportRequest).subscribe(
			({value}) => {
				this.report$.next(value);

				this.printSettings$.next({
					...this.printSettings$.value,
					reportGuid: value.reportGuid,
					gradeScaleAllow: !!value.gradeValues.length,
					markingPeriods: this.getMarkingPeriods(),
				});

				callback();
			},
		);
	}

	public onDownload(fileType: FileType) {
		this.printSettings$.next({
			...this.printSettings$.value,
			fileType: fileType,
		});

		const generateSettings = this.generateReport('download');
		this.downloadService.download(generateSettings as DownloadRequest);
	}

	private init() {
		if (
			!this.subject$.value
			|| !this.tests$.value.length
			|| !this.students$.value.length
		) {
			return;
		}

		this.inited$.next(false);
		const {id: subjectID, type: subjectType} = this.subjectService.get(this.subject$.value) || {};
		this.controller.init({
			subjectID,
			subjectType,
			studentIDs: this.students$.value.map(({studentId}) => studentId),
			testIDs: this.tests$.value,
		}).subscribe(({value: {report, teacher}}) => {
			const {
				sortBy,
				testResultsCorrectVerbiage,
				testResultsIncorrectVerbiage,
			} = teacher;
			this.printSettings$.next({
				...this.printSettings$.value,
				sortBy,
				printInColor: true,
				testResultsCorrectVerbiage,
				testResultsIncorrectVerbiage,
			});
			this.grades$.next(report.gradeValues);
			this.inited$.next(true);
		});
	}

	private generateReport(type: 'download' | '' = '') {
		if (type === 'download') {
			return this.generateService.generateDownloadReport(
				this.printSettings$.value
			);
		}
		return this.generateService.generateReport(
			this.unitDataSettings$.value,
			this.printSettings$.value
		);
	}

	private getMarkingPeriods (): MarkingPeriodItem[] {
		const value = this.report$.value;

		if (!value) {
			return [];
		}

		return [
			{index: 0, title: 'B'},
			...value.trackDates.map((_, index) => ({
				index: index + 1,
				title: (index + 1).toString(),
			})),
		];
	}
}
